import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { AlertTriangle, RefreshCw } from "lucide-react";
import React from "react";
import { useTranslation } from "react-i18next";

interface ErrorFallbackProps {
  error?: Error;
  resetErrorBoundary?: () => void;
  onRetry?: () => void;
  title?: string;
  description?: string;
  showGoBack?: boolean;
  variant?: "page" | "material" | "generic";
}

export const ErrorFallback: React.FC<ErrorFallbackProps> = ({
  error,
  resetErrorBoundary,
  onRetry,
  title,
  description,
  showGoBack = true,
  variant = "generic",
}) => {
  const { t } = useTranslation("common");

  const handleRetry = () => {
    if (onRetry) {
      onRetry();
    } else if (resetErrorBoundary) {
      resetErrorBoundary();
    } else {
      window.location.reload();
    }
  };

  // Parse HTTP status code from error message
  const getHttpErrorDetails = (errorMessage: string | undefined) => {
    if (!errorMessage) return null;

    const statusMatch = errorMessage.match(/status code (\d{3})/i);
    if (!statusMatch) return null;

    const statusCode = parseInt(statusMatch[1]);
    const errorMessages: Record<number, string> = {
      400: "Bad Request - The request was invalid or cannot be processed.",
      401: "Unauthorized - You need to log in to access this resource.",
      403: "Forbidden - You don't have permission to access this resource.",
      404: "Not Found - The requested resource could not be found.",
      408: "Request Timeout - The request took too long to complete.",
      429: "Too Many Requests - You've made too many requests. Please try again later.",
      500: "Internal Server Error - Something went wrong on the server.",
      502: "Bad Gateway - The server received an invalid response.",
      503: "Service Unavailable - The service is temporarily unavailable.",
      504: "Gateway Timeout - The server took too long to respond.",
    };

    return {
      statusCode,
      message: errorMessages[statusCode] || `HTTP Error ${statusCode}`,
    };
  };

  const httpError = getHttpErrorDetails(error?.message);

  const errorTitle =
    title ||
    (httpError
      ? `Error ${httpError.statusCode}`
      : variant === "page"
        ? t("errorFallback.pageError")
        : variant === "material"
          ? t("errorFallback.materialError")
          : t("errorFallback.genericError"));

  const errorDescription =
    description ||
    (httpError
      ? httpError.message
      : error?.message ||
        (variant === "page"
          ? t("errorFallback.pageErrorDescription")
          : variant === "material"
            ? t("errorFallback.materialErrorDescription")
            : t("errorFallback.genericError")));

  return (
    <div className="m-4 flex min-h-96 items-center justify-center">
      <Card className="border-destructive/50 bg-destructive/20 max-w-2xl">
        <CardContent className="flex flex-col items-center justify-center p-12">
          <div className="space-y-4 text-center">
            <div className="flex justify-center">
              <AlertTriangle className="h-12 w-12 text-red-700" />
            </div>

            <div className="space-y-2">
              <h3 className="text-lg font-semibold text-red-700">
                {errorTitle}
              </h3>
              <p className="max-w-md text-sm text-white">{errorDescription}</p>
            </div>

            <div className="flex justify-center gap-3 pt-2">
              <Button
                onClick={handleRetry}
                variant="secondary"
                className="gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                {t("errorFallback.tryAgain")}
              </Button>

              {showGoBack && (
                <Button
                  onClick={() => window.history.back()}
                  variant="secondary"
                >
                  {t("errorFallback.goBack")}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ErrorFallback;
